import React from 'react';

import {useSelector} from '@xstate/react';
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm, FormProvider} from 'react-hook-form';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {FormInput} from '../../lib/form-input/form-input';
import {useRootMachine} from '../../hooks/useRootMachine';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {BrandButton} from '../../lib/brand-button/brand-button';
import {getFormSchema} from '../booking-form/booking-form.schema';

import {selectCountryCode, selectError} from '../../selectors/commonSelector';
import {selectBookingNotifyHostInputs} from '../../selectors/booking-selector';
import {getDefaultFormValues} from '../../utils/form';
import {selectSession} from '../../selectors/session-selector';
import {selectEventDetails} from '../../selectors/eventDetails';

import {SubTitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import css from './booking-notify-host-form.css';


export const BookingNotifyHostForm = () => {
  const {t, language} = useLabelTranslation();
  const {bookingActor, bookingState} = useBookingMachine();
  const {rootActor} = useRootMachine();
  const {errorMessage} = useSelector(bookingActor, (state) =>
    selectError(state, t),
  );
  const countyCode = useSelector(rootActor, (state) =>
    selectCountryCode(state),
  );

  const formInputs = useSelector(bookingActor, (state) =>
    selectBookingNotifyHostInputs(state, t),
  );
  const formSchema = getFormSchema({items: formInputs, countyCode, t});

  const session = useSelector(rootActor, (state) => selectSession(state));
  const eventDetails = useSelector(rootActor, (state) =>
    selectEventDetails(state),
  );

  const {defaultValues, disabledFields} = getDefaultFormValues(
    formInputs,
    session,
    eventDetails,
  );

  const methods = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: zodResolver(formSchema),
  });

  // recreate form when language changes because React Hook Form caches error messages in its internal state
  React.useEffect(() => {
    methods.reset();
  }, [language, methods]);

  const onSubmit = () => {
    const formValues = methods.getValues();
    const {guestName, guestEmail, guestPhone} = formValues;

    bookingActor.send({
      type: 'notify.submit',
      guestName: guestName as string,
      guestEmail: guestEmail as string,
      guestPhone: guestPhone as string,
    });
  };

  const hasError = bookingState.matches({notify: 'error'});

  return (
    <FormProvider {...methods}>
      <form
        className={css.formContainer}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <div className={css.formContent}>
          <div className={css.questionContainer}>
            <SubTitleMedium>
              {t(
                'Please_answer_the_following_questions',
                'Please answer the following questions',
              )}
            </SubTitleMedium>
            {formInputs.map((item) => (
              <FormInput
                key={item.name}
                item={item}
                disabled={disabledFields[item.name]}
              />
            ))}
          </div>
        </div>
        <div className={css.submitButtonSection}>
          <BrandButton
            isFluid
            actionType="submit"
            isLoading={bookingState.matches({notify: 'submitting'})}
          >
            {t('Submit', 'Submit')}
          </BrandButton>
          {hasError && (
            <InContextAlert leftIconName="shield-exclamation" semantic="danger">
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {errorMessage}
            </InContextAlert>
          )}
        </div>
      </form>
    </FormProvider>
  );
};
